// Pure, local calculation functions for the newer Canadian tools (2026).
// No inputs ever leave the browser.
import { estimateTax } from "../tax";
import { PAYROLL } from "../financial-data/canada";

// 1. Credit utilization ------------------------------------------------------
export function creditUtilization({ cards = [] }) {
  const totalLimit = cards.reduce((s, c) => s + (+c.limit || 0), 0);
  const totalBalance = cards.reduce((s, c) => s + (+c.balance || 0), 0);
  const utilization = totalLimit > 0 ? totalBalance / totalLimit : 0;
  const payToTarget = (t) => Math.max(0, totalBalance - t * totalLimit);
  return {
    totalLimit,
    totalBalance,
    utilization,
    payToThirty: payToTarget(0.30),
    payToTen: payToTarget(0.10),
    perCard: cards.map((c) => ({
      name: c.name || "Card",
      util: +c.limit > 0 ? +c.balance / +c.limit : 0,
    })),
  };
}

// 2. Life insurance needs (DIME) --------------------------------------------
export function lifeInsuranceNeeds({
  income = 0, years = 10, mortgage = 0, otherDebts = 0,
  children = 0, eduPerChild = 0, finalExpenses = 25000,
  existingCoverage = 0, savings = 0,
}) {
  const incomeReplacement = income * years;
  const education = children * eduPerChild;
  const gross = incomeReplacement + mortgage + otherDebts + education + finalExpenses;
  const offsets = existingCoverage + savings;
  return {
    need: Math.max(0, gross - offsets),
    incomeReplacement, mortgage, otherDebts, education, finalExpenses, offsets,
  };
}

// 3. Rent affordability ------------------------------------------------------
export function rentAffordability({ monthlyIncome = 0, monthlyDebts = 0 }) {
  const rec = monthlyIncome * 0.30;
  const comfortable = monthlyIncome * 0.28;
  const stretch = monthlyIncome * 0.35;
  const maxWithDebts = Math.max(0, monthlyIncome * 0.40 - monthlyDebts);
  const affordable = Math.min(rec, maxWithDebts);
  return { rec, comfortable, stretch, affordable, leftover: Math.max(0, monthlyIncome - affordable - monthlyDebts) };
}

// 4. Freelancer / self-employed tax -----------------------------------------
export function freelancerTax({ grossIncome = 0, expenses = 0, province = "ON" }) {
  const netIncome = Math.max(0, grossIncome - expenses);
  const tax = estimateTax(netIncome, province);
  const { cpp } = PAYROLL;
  // Self-employed pay BOTH the employee + employer share (2x rate).
  const base = Math.max(0, Math.min(netIncome, cpp.ympe) - cpp.basicExemption);
  const cppBase = base * cpp.rate * 2;
  const cpp2Base = Math.max(0, Math.min(netIncome, cpp.yampe) - cpp.ympe) * cpp.cpp2Rate * 2;
  const cppTotal = cppBase + cpp2Base;
  const totalTax = tax.totalTax + cppTotal;
  return {
    netIncome,
    incomeTax: tax.totalTax,
    cpp: cppTotal,
    totalTax,
    afterTax: netIncome - totalTax,
    setAsideRate: netIncome > 0 ? totalTax / netIncome : 0,
    marginalRate: tax.marginalRate,
  };
}

// 5. First-time home buyer incentive / programs -----------------------------
export function firstTimeHomeBuyer({ price = 500000, saved = 0 }) {
  let minDown;
  if (price <= 500000) minDown = price * 0.05;
  else if (price < 1500000) minDown = 500000 * 0.05 + (price - 500000) * 0.10;
  else minDown = price * 0.20;
  const hbpMax = 60000; // RRSP Home Buyers' Plan (2024+)
  const fhsaMax = 40000; // FHSA lifetime
  const hbtc = 1500;     // First-Time Home Buyers' Tax Credit ($10,000 x 15%)
  const govHelp = hbpMax + fhsaMax + hbtc;
  return {
    minDown,
    downPct: price > 0 ? minDown / price : 0,
    hbpMax, fhsaMax, hbtc, govHelp,
    shortfall: Math.max(0, minDown - saved),
  };
}

// 6. Inflation impact --------------------------------------------------------
export function inflationImpact({ amount = 0, rate = 3, years = 10 }) {
  const i = rate / 100;
  const futureCost = amount * Math.pow(1 + i, years);
  const purchasingPower = amount / Math.pow(1 + i, years);
  const series = [];
  for (let y = 0; y <= years; y++) {
    series.push({
      x: `Y${y}`,
      Cost: Math.round(amount * Math.pow(1 + i, y)),
      Power: Math.round(amount / Math.pow(1 + i, y)),
    });
  }
  return { futureCost, purchasingPower, lost: futureCost - amount, series };
}

// 7. Debt-to-income ratio ----------------------------------------------------
export function debtToIncome({ monthlyIncome = 0, housing = 0, otherDebts = 0 }) {
  const gds = monthlyIncome > 0 ? housing / monthlyIncome : 0;
  const tds = monthlyIncome > 0 ? (housing + otherDebts) / monthlyIncome : 0;
  let rating = "Excellent";
  if (tds > 0.44) rating = "High — many lenders decline";
  else if (tds > 0.36) rating = "Moderate";
  else if (tds > 0.25) rating = "Good";
  return { gds, tds, rating, totalDebt: housing + otherDebts };
}
